import { reducer as rsfFormReducer } from "common/reducers/rsfForm.reducer";
import {
  DESTROY_IMAGE,
  EDIT_DISPLAY_BOOLEAN,
  EDIT_IMAGE,
  EDIT_IMAGES,
  ESTIMATE_LINE_ITEM_IMAGE,
  INCREASE_COUNT,
  ImageActions,
  PRODUCT_IMAGE,
  RECEIVE_CUSTOMER_IMAGE_STATUS_BY_TASK_ID,
  REMOVE_IMAGE,
  RESET_COUNT,
  SET_IMAGE_LOADED,
  SET_IMAGE_LOADING,
  TOGGLE_ALL_IMAGES_DISPLAY,
  UPDATE_IMAGE_WITH_URLS,
} from "common/reducers/image.actions";
import { IImageData, ImageRecord, setDisplay } from "common/records/Image";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { RootActions, RootState } from "common/reducers";
import { Nullable } from "common/types";
import { List, Map, Record } from "immutable";
import { imageFromJSON } from "common/records/Image";
import { OrgRecord } from "common/records/Org";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import {
  FETCH_IMAGE,
  RECEIVE_IMAGE,
  RECEIVE_IMAGES,
  RESET_IMAGE,
  RECEIVE_IMAGE_OVERVIEW,
  SET_CUSTOMER_IMAGES_ERROR,
} from "common/reducers/image.actions";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { fileFromJSON } from "common/records/File";

const model = "images";

export const ImageStateRecord = Record({
  byId: Map<number, ImageRecord>(),
  imageOverviewsById: Map<number, ImageRecord>(),
  lastSavedById: Map<number, ImageRecord>(),
  unsavedId: -1,
  currentImageId: null as Nullable<number>,
  showInfo: false,
  loading: false,
  successCount: 0,
  customersUploadStatusByTaskId: Map<string, string>(),
  customerImageError: false,
});

export const initialState = ImageStateRecord();

export type ImageState = typeof initialState;

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }
  switch (action.type) {
    case FETCH_IMAGE:
      return fetch(state, model, imageFromJSON({ id: action.payload.id } as IImageData)) as RootState;

    case RECEIVE_IMAGE:
      const image = imageFromJSON({ ...action.payload.image });
      state = receive(state, model, image) as RootState;
      if (action.payload.pushToList) {
        state = idsManagementReducer(state, IdsManagementActions.pushId(image.id, model));
      }
      return state;

    case RECEIVE_IMAGE_OVERVIEW:
      const imageOverview = imageFromJSON({ ...action.payload.image });
      state = state.setIn([model, "imageOverviewsById", imageOverview.id], imageOverview);
      return state;

    case RESET_IMAGE:
      return reset(state, model, action.payload.id) as RootState;

    case RECEIVE_IMAGES:
      action.payload.images.forEach((image) => {
        state = reducer(state, ImageActions.receiveImage(image));
      });
      return state;

    case SET_IMAGE_LOADED:
      return loaded(state, model, action.payload.id) as RootState;

    case SET_IMAGE_LOADING:
      return state.setIn([model, "loading"], action.payload.loading);

    case INCREASE_COUNT:
      return state.updateIn([model, "successCount"], (oldValue) => oldValue + 1);

    case RESET_COUNT:
      return state.setIn([model, "successCount"], 0);

    case DESTROY_IMAGE:
      if (action.payload.image.imageable_type?.toLowerCase() === "org") {
        state = state.updateIn(["orgs", "byId", action.payload.image.imageable_id], (org: OrgRecord) => {
          return org.update("image_ids", (ids) => {
            return ids.filter((id) => id !== action.payload.image.id);
          });
        });
      }
      return state.deleteIn([model, "byId", action.payload.image.id]);

    case REMOVE_IMAGE:
      return state.deleteIn([model, "byId", action.payload.id]);

    case UPDATE_IMAGE_WITH_URLS:
      if (action.payload.results.customer_image_file) {
        state = state.setIn(
          [model, "byId", action.payload.id, "file"],
          fileFromJSON(action.payload.results.customer_image_file),
        );
      }
      return state;

    case RECEIVE_CUSTOMER_IMAGE_STATUS_BY_TASK_ID:
      const { task } = action.payload;
      state = state.setIn([model, "customersUploadStatusByTaskId", task.id], task.status);
      return state;

    case SET_CUSTOMER_IMAGES_ERROR:
      state = state.setIn([model, "customerImageError"], action.payload.value);
      return state;

    // PRODUCTS
    case PRODUCT_IMAGE:
      let imageIds = state.getIn(["products", "byId", action.payload.productId, "image_ids"]) as List<number>;
      let unsavedId = state.getIn(["images", "unsavedId"]);
      const imageData = action.payload.image;
      imageData.id = unsavedId;

      const imageRecord = imageFromJSON(imageData);

      imageIds = imageIds.unshift(unsavedId);
      state = state.setIn(["images", "byId", unsavedId], imageRecord);
      unsavedId -= 1;

      return state
        .setIn(["products", "byId", action.payload.productId, "image_ids"], imageIds)
        .setIn(["images", "unsavedId"], unsavedId);

    // ESTIMATE LINE ITEM
    case ESTIMATE_LINE_ITEM_IMAGE: {
      const lineItemId = action.payload.estimateLineItemId;
      const lineItemImageIds = (state.getIn(["estimateLineItems", "byId", lineItemId, "image_ids"]) ??
        List<number>()) as List<number>;
      let nextUnsavedId = state.getIn([model, "unsavedId"]) as number;

      // Stamp an incrementing sort_order so newly-staged photos sort *after* already-attached ones
      // instead of colliding at 0 (BR-3). max(existing non-destroyed) + 1; an empty gallery starts at
      // 0 (reduce seed -1). An explicit payload sort_order (rare) wins.
      const imagesByIdMap = state.getIn([model, "byId"]) as Map<number, ImageRecord>;
      const maxSortOrder = lineItemImageIds.reduce((max: number, id: number) => {
        const existing = imagesByIdMap.get(id);
        if (!existing || existing._destroy) return max;
        return Math.max(max, existing.sort_order ?? 0);
      }, -1);

      const newImageData = {
        sort_order: maxSortOrder + 1,
        ...action.payload.image,
        id: nextUnsavedId,
      };
      const newImageRecord = imageFromJSON(newImageData);

      const updatedImageIds = lineItemImageIds.push(nextUnsavedId);
      state = state.setIn([model, "byId", nextUnsavedId], newImageRecord);
      nextUnsavedId -= 1;

      return state
        .setIn(["estimateLineItems", "byId", lineItemId, "image_ids"], updatedImageIds)
        .setIn([model, "unsavedId"], nextUnsavedId);
    }

    case EDIT_IMAGE: {
      const { id, name, value } = action.payload;
      return state.setIn([model, "byId", id, name], value);
    }

    case EDIT_IMAGES: {
      const { ids, name, value } = action.payload;
      return ids.reduce((acc, id) => acc.setIn([model, "byId", id, name], value), state);
    }

    case EDIT_DISPLAY_BOOLEAN: {
      const { rootPath, name } = action.payload.event;

      state = rsfFormReducer(state, RsfFormActions.update(action.payload.event));

      const record = state.getIn(rootPath) as ImageRecord;
      return state.setIn(rootPath.concat(["display"]), setDisplay(record));
    }

    case TOGGLE_ALL_IMAGES_DISPLAY: {
      action.payload.ids.forEach((id) => {
        state = reducer(
          state,
          ImageActions.editDisplayBoolean({
            rootPath: [model, "byId", id],
            name: action.payload.displayProp,
            value: action.payload.value,
          }),
        );
      });

      return state;
    }

    default:
      return state;
  }
};
